'use client'

import React, { useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  Button,
  DialogOverlay
} from '@teqplay/ui'

export type OpenConfirmModalOptions = {
  title: string | React.ReactNode
  description: string | React.ReactNode
  confirmLabel?: string
  cancelLabel?: string
  onConfirm: () => void
  onCancel?: () => void
  id?: string
  noCancel?: boolean
}

const openConfirmModalOpenIds = new Set<string>()

function canUseDOM(): boolean {
  try {
    return typeof window !== 'undefined' && !!window.document && !!window.document.createElement
  } catch {
    return false
  }
}

function ConfirmDialog(props: OpenConfirmModalOptions & { onFinalize: () => void }) {
  const {
    title,
    description,
    onConfirm,
    onCancel,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    onFinalize,
    noCancel = false
  } = props
  const [open, setOpen] = useState(true)

  // When the dialog is closed by any means, ensure cleanup happens
  useEffect(() => {
    if (!open) {
      // give Dialog time to animate out if needed
      const t = setTimeout(() => {
        onFinalize()
      }, 10)
      return () => clearTimeout(t)
    }
    return undefined
  }, [open, onFinalize])

  const handleConfirm = () => {
    try {
      onConfirm?.()
    } finally {
      setOpen(false)
    }
  }

  const handleCancel = () => {
    try {
      onCancel?.()
    } finally {
      setOpen(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogOverlay />
      <DialogContent aria-describedby="confirm-modal-description">
        <DialogHeader>
          <DialogTitle asChild>
            {typeof title === 'string' ? <h3>{title}</h3> : <>{title}</>}
          </DialogTitle>
          {description !== undefined && (
            <DialogDescription id="confirm-modal-description">
              {typeof description === 'string' ? <span>{description}</span> : <>{description}</>}
            </DialogDescription>
          )}
        </DialogHeader>
        <DialogFooter>
          {!noCancel && (
            <DialogClose asChild>
              <Button variant="brand-secondary" type="button" onClick={handleCancel}>
                {cancelLabel ?? 'Cancel'}
              </Button>
            </DialogClose>
          )}
          <Button variant="brand-primary" type="button" onClick={handleConfirm}>
            {confirmLabel ?? 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

const modalQueue: OpenConfirmModalOptions[] = []
let isShowing = false

function processQueue(options: OpenConfirmModalOptions) {
  if (isShowing || !canUseDOM()) return
  const next = modalQueue.shift()
  if (!next) return

  const id = options.id
  if (id) {
    if (openConfirmModalOpenIds.has(id)) {
      return
    }
    openConfirmModalOpenIds.add(id)
  }

  const container = document.createElement('div')
  container.setAttribute('data-confirm-modal-container', 'true')
  document.body.appendChild(container)

  const root = createRoot(container)

  const finalize = () => {
    try {
      root.unmount()
    } catch {
      // Ignore errors during unmount; cleanup is best-effort.
    }
    try {
      container.remove()
    } catch {
      // Ignore errors removing the DOM node; cleanup is best-effort.
    }

    if (id) {
      openConfirmModalOpenIds.delete(id)
    }
    isShowing = false
    // proceed with the next modal in the queue
    setTimeout(processQueue, 0)
  }

  isShowing = true
  root.render(<ConfirmDialog {...next} onFinalize={finalize} />)
}

export function openConfirmModal(options: OpenConfirmModalOptions) {
  if (!canUseDOM()) {
    options.onCancel?.()
    return
  }
  modalQueue.push(options)
  processQueue(options)
}
